import React, {useCallback} from 'react';
import {Alert, Snackbar as MuiSnackbar, SnackbarOrigin} from '@mui/material';

export enum SnackbarSeverity {
  SUCCESS = 'success',
  ERROR = 'error',
}

export type SnackbarState = {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
};

export const initialSnackbarState: SnackbarState = {
  open: false,
  message: '',
  severity: SnackbarSeverity.SUCCESS,
};

const AUTO_HIDE_DURATION_IN_MS = 8000;

interface SnackbarProps {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  onClose: () => void;
  autoHideDuration?: number;
  anchorOrigin?: SnackbarOrigin;
}

export type TSetSnackbarState = (snackbarState: SnackbarState) => void;

interface useSnackbarResult {
  snackbarState: SnackbarState;
  setSnackbarState: TSetSnackbarState;
  closeSnackbar: () => void;
  Snackbar: React.FC<SnackbarProps>;
}

export const useSnackbar = (): useSnackbarResult => {
  const [snackbar, setSnackbar] = React.useState<SnackbarState>(initialSnackbarState);

  const setSnackbarState = useCallback(
    (snackbarState: SnackbarState) => {
      setSnackbar(snackbarState);
    },
    [setSnackbar]
  );

  const closeSnackbar = useCallback(() => {
    setSnackbar((prevState) => ({...prevState, open: false}));
  }, []);

  return {snackbarState: snackbar, setSnackbarState, closeSnackbar, Snackbar};
};

const Snackbar: React.FC<SnackbarProps> = ({
  open,
  message,
  severity,
  onClose,
  autoHideDuration = AUTO_HIDE_DURATION_IN_MS,
  anchorOrigin = {vertical: 'top', horizontal: 'center'},
}) => {
  return (
    <MuiSnackbar anchorOrigin={anchorOrigin} open={open} onClose={onClose} autoHideDuration={autoHideDuration}>
      <Alert onClose={onClose} severity={severity} sx={{width: '100%'}}>
        {message}
      </Alert>
    </MuiSnackbar>
  );
};
